import dayjs from "dayjs";
import type { TFunction } from "i18next";
import type {
  IncomingValuesResponse,
  MappingLookupItem,
  MappingPayload,
} from "src/services/mappingService";
import type {
  IncomingValueOption,
  MappingAddEditValues,
  MappingFilterRecord,
  MappingFormPrimitive,
  MappingOption,
  MappingRecord,
  MappingSelectOption,
} from "./mapping.types";

export const safeString = (value: unknown) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

export const formatDateToOurFormat2 = (dateString?: string, format = "YYYY-MM-DD HH:mm:ss") => {
  if (!dateString) return "--";
  return dayjs(dateString).isValid() ? dayjs(dateString).format(format) : "Invalid Date";
};

export const getObjectById = <T extends { id?: unknown; value?: unknown }>(
  array: T[] | undefined,
  id: unknown
) => array?.find((item) => (item.id ?? item.value) === id);

export const addMappingOptions: MappingOption[] = [
  { name: "GPS Status", id: "gps_status" },
  { name: "GPS Blocked", id: "gps_blocked" },
  { name: "GSM Signal strength", id: "gsm_signal_strength" },
  { name: "Ignition start/stop events", id: "ignition_start_stop_events" },
  { name: "Power source voltage", id: "power_source_voltage" },
  { name: "External power events", id: "external_power_events" },
  { name: "CAN Data", id: "can_data" },
  { name: "Relay Status", id: "relay_status" },
  { name: "PTO", id: "pto" },
  {
    name: "Save Value for Readables Value",
    id: "save_value_for_readables_value",
  },
  { name: "Switch ON/OFF State", id: "switch_on_off_state" },
  { name: "Temperature", id: "temperature" },
  { name: "Weight on Axis (single Value)", id: "weight_on_axis_single_value" },
  { name: "Fuel Level Sensor Data", id: "fuel_level_sensor_data" },
  { name: "Fuel Level (%) CAN", id: "fuel_level_percentage_can" },
  { name: "Fuel Level (L) CAN", id: "fuel_level_liter_can" },
  {
    name: "Fuel Catalyst Level (%)",
    id: "fuel_catalyst_level_percentage_form",
  },
  { name: "Tacho Working State", id: "tacho_working_state" },
  { name: "Tacho Related State", id: "tacho_related_state" },
  { name: "Tacho Driver Name", id: "tacho_driver_name" },
  { name: "Tacho Driver Surname", id: "tacho_driver_surname" },
  { name: "Tacho Card Id", id: "tacho_card_id" },
  { name: "Tacho Card Presence", id: "tacho_card_presence" },
  { name: "Badge Id", id: "badge_id" },
  { name: "Badge Authorization", id: "badge_authorization" },
];

export const addFilterOptions: MappingOption[] = [
  {
    name: "Adjust Value (Offset & Multiply)",
    id: "adjust_value_offset_multiply",
  },
  { name: "Adjust Value (If Statement)", id: "adjust_value_if_statement" },
  { name: "Invert Value", id: "invert_value" },
  { name: "Allowed Range Inclusive", id: "allowed_range_inclusive" },
];

export const externalPowerEventsOptions: MappingSelectOption[] = [
  { label: "Yes", value: "true" },
  { label: "No", value: "false" },
];

export const fieldSourceOptions: MappingSelectOption[] = [
  { label: "Power", value: "power" },
  { label: "Battery", value: "battery" },
  { label: "Battery %", value: "battery_percentage" },
];

export const signalStrengthFieldTypeOption: MappingSelectOption[] = [
  { label: "RSSI Level", value: "1" },
  { label: "GSM (Teltonika)", value: "2" },
];

export const fieldTypeOptions: MappingSelectOption[] = [
  { label: "Engine speed (05)", value: "05" },
  { label: "Engine RPM maximum impulses (06)", value: "06" },
  { label: "Service distance (08)", value: "08" },
  { label: "Total driving distance value (09)", value: "09" },
  { label: "Fuel level CAN (10)", value: "10" },
  { label: "Engine total hours (11)", value: "11" },
  { label: "Engine load, %", value: "engine_load" },
  { label: "LNG Fuel Level Cumulative, Kilograms (29)", value: "lng_cum" },
  { label: "CNG Fuel Level Cumulative, Kilograms (30)", value: "cng_cum" },
  { label: "Total Fuel level CAN in liters (31)", value: "lpg_cum" },
  {
    label: "EV Electrical Energy Cumulative, kWh (32)",
    value: "electricity_cum",
  },
  {
    label: "Alternative Fuel Cumulative, Liters/Kilograms/KWh (33)",
    value: "alternative_fuel_cum",
  },
  { label: "Ambient temperature", value: "ambientTemperature" },
  { label: "Combined weight", value: "combinedWeight" },
  { label: "AdBlue level CAN", value: "adblueLevel" },
  { label: "Speed CAN", value: "speedCan" },
];

export const convertOptions = externalPowerEventsOptions;
export const fieldRelayIdOptions = ["1", "2", "3", "4"].map((id) => ({
  label: `Relay ${id}`,
  value: id,
}));
export const switchStateUniqueIdOption = Array.from({ length: 15 }, (_, index) => {
  const id = String(index + 1);
  return { label: `Switch ${id}`, value: id };
});
export const fuelTankIdFieldOptions = Array.from({ length: 10 }, (_, index) => {
  const id = String(index + 1);
  return { label: `Tank ${id}`, value: id };
});
export const fuelLevelPercentageConvertJOptions = [
  { label: "No", value: "false" },
  { label: "Yes", value: "true" },
];
export const fuelCatalystPercentageOptions = fuelLevelPercentageConvertJOptions;
export const driverData = [
  { label: "Driver 1", value: "1" },
  { label: "Driver 2", value: "2" },
];
export const driverIdFormat = [
  { label: "ID", value: "id" },
  { label: "MSB", value: "msb" },
  { label: "LSB", value: "lsb" },
];
export const temperatureTypeData = [
  { label: "Ambient", value: "ambient" },
  { label: "Engine", value: "engine" },
  { label: "Fridge", value: "fridge" },
  { label: "Cargo", value: "cargo" },
  { label: "Emulsion", value: "emulsion" },
];
export const relayStatusTypeData = [
  { label: "Ignition Lock", value: "ignition_lock" },
  { label: "Equipment Lock", value: "equipment_lock" },
  { label: "Control Lock", value: "control_lock" },
  { label: "Pumping Lock", value: "pumping_lock" },
  {
    label: "Power Reduction (machine throttle lock)",
    value: "power_reduction_machine_throttle_lock",
  },
  { label: "Buzzer Activation", value: "buzzer_activation" },
  { label: "LED Activation", value: "led_activation" },
];
export const temperatureUnitData = [
  { label: "C", value: "c" },
  { label: "F", value: "f" },
];

const optionKeyMap: Record<string, MappingSelectOption[]> = {
  field_Type: signalStrengthFieldTypeOption,
  field_Source: fieldSourceOptions,
  field_Save_only_when_changes: externalPowerEventsOptions,
  canDataConvert: convertOptions,
  canDataFieldType: fieldTypeOptions,
  relayId: fieldRelayIdOptions,
  switchStateUniqueId: switchStateUniqueIdOption,
  type: relayStatusTypeData,
  field_tank_ID: fuelTankIdFieldOptions,
  fuelTankIdField: fuelTankIdFieldOptions,
  fuelLevePercentageCan: fuelLevelPercentageConvertJOptions,
  fuelCatalystLevelPercentage: fuelCatalystPercentageOptions,
};

export const getOptionsByKey = (key: string) => optionKeyMap[key] || [];

export const toLookupOptions = (items?: MappingLookupItem[]): MappingSelectOption[] =>
  (items ?? []).map((item) => ({
    value: String(item.code ?? item.name ?? item.id),
    label: item.name ?? item.code ?? String(item.id),
    code: item.code,
    name: item.name,
  }));

export const normalizeFormPrimitive = (value: unknown): MappingFormPrimitive => {
  if (
    value === undefined ||
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }

  return 0;
};

const normalizeFilterFieldValue = (key: string, value: unknown): MappingFormPrimitive => {
  if (
    key === "operator" &&
    value &&
    typeof value === "object" &&
    "id" in value &&
    typeof value.id === "string"
  ) {
    return value.id;
  }

  return normalizeFormPrimitive(value);
};

const getFormPrimitive = (
  values: MappingAddEditValues,
  key: string,
  fallback: MappingFormPrimitive
) => {
  const value = values[key];
  return Array.isArray(value) ? fallback : normalizeFormPrimitive(value ?? fallback);
};

const getFormStringValue = (
  values: MappingAddEditValues,
  key: string,
  fallback: MappingFormPrimitive
) => {
  const value = getFormPrimitive(values, key, fallback);
  return value === undefined || value === null ? String(fallback ?? "") : String(value);
};

const ifStatementOperatorPayloadOptions = [
  { id: "greater_than", name: ">" },
  { id: "less_than", name: "<" },
  { id: "greater_than_equal", name: ">=" },
  { id: "less_than_equal", name: "<=" },
  { id: "equal", name: "=" },
] as const;

const getIfStatementOperatorId = (value: unknown) => {
  if (!value) return null;

  if (typeof value === "object") {
    const option = value as { id?: unknown; value?: unknown };
    const operatorId = option.id ?? option.value;

    return operatorId === undefined || operatorId === null || operatorId === ""
      ? null
      : String(operatorId);
  }

  return String(value);
};

export const getIfStatementOperatorLabel = (value: unknown) => {
  const operatorId = getIfStatementOperatorId(value);
  if (!operatorId) return "";

  return (
    ifStatementOperatorPayloadOptions.find((operator) => operator.id === operatorId)?.name ??
    operatorId
  );
};

const getIfStatementOperatorPayload = (value: unknown) => {
  const operatorId = getIfStatementOperatorId(value);
  if (!operatorId) return null;

  return (
    ifStatementOperatorPayloadOptions.find((operator) => operator.id === operatorId) ?? {
      id: operatorId,
      name: operatorId,
    }
  );
};

const createFilterUniqueId = (filterId: string) =>
  `${filterId}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

export const makeFilter = (filterId: string): MappingFilterRecord => ({
  id: filterId,
  uniqueId: createFilterUniqueId(filterId),
  fields: {},
});

export const getMappingFilters = (mapping?: MappingRecord): MappingFilterRecord[] => {
  const savedFilters = Array.isArray(mapping?.filters)
    ? mapping.filters
    : Array.isArray(mapping?.filter)
      ? mapping.filter
      : [];

  return savedFilters.map((filter) => ({
    id: filter.id,
    uniqueId: filter.uniqueId ?? createFilterUniqueId(filter.id),
    fields: Object.fromEntries(
      Object.entries(filter.fields ?? {}).map(([key, value]) => [
        key,
        normalizeFilterFieldValue(key, value),
      ])
    ),
  }));
};

export const getInitialMappingValues = (
  mapping: MappingRecord,
  incomingOption?: IncomingValueOption
): MappingAddEditValues => {
  const fields = mapping.mappingFields ?? {};
  const filters = getMappingFilters(mapping);
  const values: MappingAddEditValues = {
    selectMapping: mapping.mappingTypeId,
    value: incomingOption?.id,
    packetCode: mapping.packetCode,
    lastValue: incomingOption?.lastValue,
    realtime: mapping.realTime,
    targetCheckbox: Boolean(mapping.forwardTo),
    targetInput: mapping.forwardTo,
    filterType: filters,
  };

  Object.entries(fields).forEach(([key, value]) => {
    values[key] = normalizeFormPrimitive(value);
  });

  if (mapping.mappingTypeId === "tacho_driver_name")
    values.name_driver = normalizeFormPrimitive(fields.driver);
  if (mapping.mappingTypeId === "tacho_driver_surname")
    values.surname_driver = normalizeFormPrimitive(fields.driver);
  if (mapping.mappingTypeId === "tacho_working_state")
    values.working_state_driver = normalizeFormPrimitive(fields.driver);
  if (mapping.mappingTypeId === "tacho_related_state")
    values.related_state_driver = normalizeFormPrimitive(fields.driver);
  if (mapping.mappingTypeId === "tacho_card_presence")
    values.card_presence_driver = normalizeFormPrimitive(fields.driver);
  if (mapping.mappingTypeId === "tacho_card_id") {
    values.driver_id_driver = normalizeFormPrimitive(fields.driver);
    values.driver_id_format = normalizeFormPrimitive(fields.type);
  }

  filters.forEach((filter) => {
    const uniqueId = filter.uniqueId ?? filter.id;
    Object.entries(filter.fields ?? {}).forEach(([key, value]) => {
      values[`${uniqueId}_${key}`] = value;
    });
  });

  return values;
};

export const getInitialMappingValuesFromCardContext = (
  context: {
    childKey: string;
    packetCode?: string;
    lastValue: MappingFormPrimitive;
    currentValue?: MappingFormPrimitive;
  } | null,
  incomingOptions: IncomingValueOption[]
): MappingAddEditValues => {
  if (!context) {
    return {
      realtime: false,
      targetCheckbox: false,
      targetInput: "",
      filterType: [],
    };
  }

  const incomingOption = incomingOptions.find((item) => item.id === context.childKey);
  const lastValue = context.lastValue ?? context.currentValue ?? incomingOption?.lastValue;

  return {
    value: incomingOption?.id ?? context.childKey,
    packetCode: context.packetCode ?? incomingOption?.packetCode,
    lastValue,
    realtime: false,
    targetCheckbox: false,
    targetInput: "",
    filterType: [],
  };
};

export const getIncomingOptions = (incomingValues: IncomingValuesResponse): IncomingValueOption[] =>
  Object.entries(incomingValues).flatMap(([, group]) =>
    Object.entries(group).map(([name, entry]) => ({
      id: name,
      name,
      packetCode: String(entry.packet_code ?? ""),
      lastValue:
        Array.isArray(entry.data) && entry.data.length > 0
          ? normalizeFormPrimitive(entry.data.find(Boolean)?.value)
          : undefined,
      entry,
    }))
  );

export const getMappingsByPacketCode = (mappings: MappingRecord[], packetCode?: string) =>
  mappings.filter((mapping) => String(mapping.packetCode) === String(packetCode));

export const isPacketCodePresent = (mappings: MappingRecord[], packetCode?: string) =>
  mappings.some((mapping) => String(mapping.packetCode) === String(packetCode));

export const getIncomingOptionForMapping = (
  incomingOptions: IncomingValueOption[],
  mapping: MappingRecord,
  preferredIncomingId?: string
) => {
  const mappingPacketCode = String(mapping.packetCode ?? "");

  return (
    incomingOptions.find((item) => item.id === preferredIncomingId) ??
    incomingOptions.find((item) => String(item.packetCode) === mappingPacketCode) ??
    incomingOptions.find(
      (item) => String(item.id) === mappingPacketCode || String(item.name) === mappingPacketCode
    )
  );
};

export const getMappingName = (mappingTypeId?: string, t?: TFunction) => {
  const option = addMappingOptions.find((item) => item.id === mappingTypeId);
  if (!option) return mappingTypeId ?? "";

  return t?.(`mapping_type_${option.id}`, { defaultValue: option.name }) ?? option.name;
};

export const transformMappingDrawerData = (
  incomingValues: IncomingValuesResponse,
  mappings: MappingRecord[]
) =>
  Object.entries(incomingValues)
    .map(([parentName, parentValue]) => ({
      parentName,
      children: Object.entries(parentValue)
        .map(([name, childValue]) => ({
          name,
          data: getMappingsByPacketCode(mappings, childValue.packet_code),
        }))
        .filter((child) => child.data.length > 0),
    }))
    .filter((parent) => parent.children.length > 0);

export const MAPPING_SPECIFIC_FIELD_NAMES = [
  "field_Type",
  "field_Source",
  "field_Save_only_when_changes",
  "canDataConvert",
  "canDataFieldType",
  "disabledNotificationAlert",
  "relayId",
  "valueOff",
  "valueOn",
  "label",
  "readableValueName",
  "switchStateFieldType",
  "type",
  "switchStateUniqueId",
  "switchStateValueOff",
  "valueOnLabel",
  "field_tank_ID",
  "wheelSingleValue",
  "axisSingleValue",
  "fuelTankIdField",
  "fuelLevePercentageCan",
  "fuelCatalystLevelPercentage",
  "relayLabel",
  "valueOffLabel",
  "sendCommandWhileDriving",
  "tankCapacity",
  "calibration",
  "enableRefueling",
  "medianWidth",
  "maxDecSpeed",
  "maxIncSpeed",
  "unit",
  "name_driver",
  "surname_driver",
  "driver_id_driver",
  "driver_id_format",
  "card_presence_driver",
  "working_state_driver",
  "related_state_driver",
  "linkedToFuel",
  "fuel_consumed",
] as const;

const emptyMappingFieldValues = MAPPING_SPECIFIC_FIELD_NAMES.reduce<Partial<MappingAddEditValues>>(
  (values, fieldName) => ({
    ...values,
    [fieldName]: undefined,
  }),
  {}
);

export const getDefaultMappingFieldValues = (
  mappingTypeId?: string
): Partial<MappingAddEditValues> => {
  const values: Partial<MappingAddEditValues> = {
    ...emptyMappingFieldValues,
    linkedToFuel: false,
    fuel_consumed: "",
  };

  if (mappingTypeId === "relay_status") {
    values.sendCommandWhileDriving = false;
  }

  if (mappingTypeId === "switch_on_off_state") {
    values.linkedToFuel = false;
    values.fuel_consumed = "";
  }

  if (mappingTypeId === "temperature") {
    values.unit = "c";
  }

  if (mappingTypeId === "fuel_level_sensor_data") {
    values.medianWidth = 3;
    values.maxIncSpeed = 120;
    values.maxDecSpeed = 60;
    values.enableRefueling = false;
  }

  if (
    mappingTypeId === "fuel_level_percentage_can" ||
    mappingTypeId === "fuel_level_liter_can"
  ) {
    values.tankCapacity = 120;
    values.medianWidth = 3;
    values.maxIncSpeed = 120;
    values.maxDecSpeed = 60;
    values.enableRefueling = false;
  }

  return values;
};

const isPresent = (value: unknown) => value !== undefined && value !== null && value !== "";

const getOptionValue = (value: unknown): MappingFormPrimitive => {
  if (value && typeof value === "object") {
    const option = value as {
      id?: unknown;
      value?: unknown;
      code?: unknown;
      name?: unknown;
    };
    return normalizeFormPrimitive(option.id ?? option.value ?? option.code ?? option.name);
  }

  return normalizeFormPrimitive(value);
};

const getCodeOrNameValue = (value: unknown): MappingFormPrimitive => {
  if (value && typeof value === "object") {
    const option = value as {
      code?: unknown;
      name?: unknown;
      id?: unknown;
      value?: unknown;
    };
    return normalizeFormPrimitive(option.code ?? option.name ?? option.id ?? option.value);
  }

  return normalizeFormPrimitive(value);
};

export const createMappingFields = (
  data: MappingAddEditValues,
  mappingTypeId?: string
): Record<string, unknown> => {
  const mappingFields: Record<string, unknown> = {};

  const copyPrimitive = (fieldName: string, targetName = fieldName) => {
    const value = data[fieldName];
    if (isPresent(value)) mappingFields[targetName] = value;
  };

  const copyOptionId = (fieldName: string, targetName = fieldName) => {
    const value = getOptionValue(data[fieldName]);
    if (isPresent(value)) mappingFields[targetName] = value;
  };

  const copyCodeOrName = (fieldName: string, targetName = fieldName) => {
    const value = getCodeOrNameValue(data[fieldName]);
    if (isPresent(value)) mappingFields[targetName] = value;
  };

  const copyTruthy = (fieldName: string, targetName = fieldName) => {
    const value = data[fieldName];
    if (value) mappingFields[targetName] = value;
  };

  switch (mappingTypeId) {
    case "gsm_signal_strength":
      copyOptionId("field_Type");
      break;

    case "power_source_voltage":
      copyOptionId("field_Source");
      break;

    case "external_power_events":
      copyOptionId("field_Save_only_when_changes");
      break;

    case "can_data":
      copyOptionId("canDataConvert");
      copyOptionId("canDataFieldType");
      break;

    case "relay_status":
      if (typeof data.disabledNotificationAlert === "boolean") {
        mappingFields.disabledNotificationAlert = data.disabledNotificationAlert;
      }
      copyOptionId("relayId");
      copyPrimitive("relayLabel");
      copyPrimitive("valueOnLabel");
      copyPrimitive("valueOffLabel");
      if (data.sendCommandWhileDriving) {
        mappingFields.sendCommandWhileDriving = data.sendCommandWhileDriving;
      }
      copyCodeOrName("type");
      break;

    case "pto":
      copyPrimitive("label");
      copyPrimitive("valueOn");
      copyPrimitive("valueOff");
      break;

    case "save_value_for_readables_value":
      copyPrimitive("readableValueName");
      break;

    case "switch_on_off_state":
      copyPrimitive("label");
      copyPrimitive("valueOnLabel");
      copyPrimitive("switchStateValueOff");
      copyOptionId("switchStateUniqueId");
      copyCodeOrName("switchStateFieldType");
      break;

    case "temperature":
      copyPrimitive("label");
      copyOptionId("field_tank_ID");
      copyOptionId("unit");
      copyCodeOrName("type");
      break;

    case "weight_on_axis_single_value":
      copyPrimitive("axisSingleValue");
      copyPrimitive("wheelSingleValue");
      break;

    case "fuel_level_sensor_data":
      copyOptionId("fuelTankIdField");
      copyPrimitive("label");
      copyOptionId("calibration");
      if (data.enableRefueling) mappingFields.enableRefueling = data.enableRefueling;
      copyTruthy("medianWidth");
      copyTruthy("maxDecSpeed");
      copyTruthy("maxIncSpeed");
      break;

    case "fuel_level_percentage_can":
    case "fuel_level_liter_can":
      copyPrimitive("label");
      copyTruthy("tankCapacity");
      if (data.enableRefueling) mappingFields.enableRefueling = data.enableRefueling;
      copyTruthy("medianWidth");
      copyTruthy("maxDecSpeed");
      copyTruthy("maxIncSpeed");
      break;

    case "fuel_catalyst_level_percentage_form":
      break;

    case "tacho_driver_name":
      copyOptionId("name_driver", "driver");
      break;

    case "tacho_driver_surname":
      copyOptionId("surname_driver", "driver");
      break;

    case "tacho_working_state":
      copyOptionId("working_state_driver", "driver");
      break;

    case "tacho_related_state":
      copyOptionId("related_state_driver", "driver");
      break;

    case "tacho_card_presence":
      copyOptionId("card_presence_driver", "driver");
      break;

    case "tacho_card_id":
      copyOptionId("driver_id_driver", "driver");
      copyOptionId("driver_id_format", "type");
      break;

    default:
      break;
  }

  mappingFields.linkedToFuel = Boolean(data.linkedToFuel);
  if (data.linkedToFuel && data.fuel_consumed !== undefined && data.fuel_consumed !== "") {
    mappingFields.fuel_consumed = Number(data.fuel_consumed);
  }

  return mappingFields;
};

export const buildFilterPayload = (
  filters: MappingFilterRecord[] | undefined,
  values: MappingAddEditValues
): MappingPayload["filters"] =>
  filters?.map((filter) => {
    const uniqueId = filter.uniqueId ?? filter.id;

    const fields: Record<string, unknown> =
      filter.id === "adjust_value_offset_multiply"
        ? {
            offset: getFormStringValue(values, `${uniqueId}_offset`, "0"),
            multiplier: getFormStringValue(values, `${uniqueId}_multiplier`, "1"),
          }
        : filter.id === "adjust_value_if_statement"
          ? {
              operator: getIfStatementOperatorPayload(values[`${uniqueId}_operator`]),
              param: getFormStringValue(values, `${uniqueId}_param`, "0"),
              transformedValue: getFormStringValue(values, `${uniqueId}_transformedValue`, "0"),
            }
          : filter.id === "allowed_range_inclusive"
            ? {
                minValue: getFormStringValue(values, `${uniqueId}_minValue`, "0"),
                maxValue: getFormStringValue(values, `${uniqueId}_maxValue`, "0"),
                returnNearest: getFormPrimitive(values, `${uniqueId}_returnNearest`, false),
              }
            : {};

    return {
      id: filter.id,
      uniqueId: filter.uniqueId,
      fields,
    };
  });

export const buildMappingSubmitPayload = (
  values: MappingAddEditValues,
  incomingOptions: IncomingValueOption[],
  unitId: string | number,
  editingMapping?: MappingRecord | null
) => {
  const incomingOption = incomingOptions.find((item) => item.id === values.value);
  const formFilters = values.filterType ?? getMappingFilters(editingMapping ?? undefined);
  const filterPayload = buildFilterPayload(formFilters, values);
  const mappingFields = createMappingFields(
    values,
    values.selectMapping ?? editingMapping?.mappingTypeId
  );
  const hasFilters = Array.isArray(filterPayload) && filterPayload.length > 0;

  const payload: MappingPayload = {
    unitId,
    mappingTypeId: values.selectMapping ?? "",
    realTime: values.realtime ? values.realtime : false,
    forwardTo: values.targetCheckbox ? String(values.targetInput ?? "") : "",
    packetCode: incomingOption?.packetCode ?? String(values.packetCode ?? ""),
    ...(hasFilters ? { filters: filterPayload } : {}),
    mappingFields,
  };

  const updatePayload: Omit<MappingPayload, "unitId"> = {
    ...payload,
    mappingTypeId: editingMapping?.mappingTypeId ?? payload.mappingTypeId,
  };
  delete (updatePayload as Partial<MappingPayload>).unitId;

  return {
    payload,
    updatePayload,
  };
};

interface DeleteMappingWithFeedbackParams {
  mapping: MappingRecord;
  unitId: string | number;
  deleteMapping: (params: { id: string | number; unitId: string | number }) => Promise<unknown>;
  message: {
    success: (content: string) => void;
    error: (content: string) => void;
  };
  t: TFunction;
  onSuccess?: () => void;
}

export const deleteMappingWithFeedback = async ({
  mapping,
  unitId,
  deleteMapping,
  message,
  t,
  onSuccess,
}: DeleteMappingWithFeedbackParams) => {
  try {
    await deleteMapping({ id: mapping.id, unitId });
    message.success("Mapping Deleted Successfully!");
    onSuccess?.();
  } catch {
    message.error(t("error"));
  }
};

export const applyFilterPreview = (
  filter: MappingFilterRecord,
  value: unknown,
  values: MappingAddEditValues
) => {
  const numericValue = Number(value);
  const uniqueId = filter.uniqueId ?? filter.id;

  switch (filter.id) {
    case "adjust_value_offset_multiply":
      return (
        (numericValue + Number(values[`${uniqueId}_offset`] ?? 0)) *
        Number(values[`${uniqueId}_multiplier`] ?? 1)
      );
    case "adjust_value_if_statement": {
      const param = Number(values[`${uniqueId}_param`] ?? 0);
      const operator = getIfStatementOperatorId(values[`${uniqueId}_operator`]);
      const matches =
        operator === "greater_than"
          ? numericValue > param
          : operator === "greater_than_equal"
            ? numericValue >= param
            : operator === "less_than"
              ? numericValue < param
              : operator === "less_than_equal"
                ? numericValue <= param
                : operator === "equal"
                  ? numericValue === param
                  : false;

      return matches ? Number(values[`${uniqueId}_transformedValue`] ?? 0) : numericValue;
    }
    case "invert_value":
      return numericValue === 0 ? 1 : 0;
    case "allowed_range_inclusive": {
      const minValue = Number(values[`${uniqueId}_minValue`] ?? 0);
      const maxValue = Number(values[`${uniqueId}_maxValue`] ?? 0);
      return numericValue >= minValue && numericValue <= maxValue ? numericValue : " ";
    }
    default:
      return value;
  }
};

export const getSwitchStateStaticOptions = (t: TFunction): MappingSelectOption[] => {
  const options: Array<[string, string]> = [
    ["power_take_off", "power_take_off"],
    ["compaction_cycle", "compaction_cycle"],
    ["bin_lifting", "bin_lifting"],
    ["hooklif_arm", "hooklif_arm"],
    ["water_pump", "water_pump"],
    ["vacuum_pump", "vacuum_pump"],
    ["pressure_pump", "pressure_pump"],
    ["concrete_pumping", "concrete_pumping"],
    ["concrete_loading_cycle", "concrete_loading_cycle"],
    ["concrete_idle_cycle", "concrete_idle_cycle"],
    ["hooks_operation", "hooks_operation"],
    ["hydraulic_equipment", "hydraulic_equipment"],
    ["moving_floor", "moving_floor"],
    ["door_locking", "door_locking"],
    ["door_opening", "door_opening"],
    ["tarp_system", "tarp_system"],
    ["loading_floor", "loading_floor"],
    ["unloading_floor", "unloading_floor"],
    ["extended_crane", "extended_crane"],
    ["tipping_trailer", "tipping_trailer"],
    ["hopper", "hopper"],
    ["brushes", "brushes"],
    ["arm", "arm"],
    ["rotating_arm", "rotating_arm"],
    ["roller_vibration", "roller_vibration"],
    ["concrete_spreading", "concrete_spreading"],
    ["auxiliary_engine", "auxiliary_engine"],
    ["side_brushes", "side_brushes"],
    ["suction", "suction"],
    ["milling_drum", "milling_drum"],
    ["emulsion_heating", "emulsion_heating"],
    ["turbine", "turbine"],
    ["water_reservoir", "water_reservoir"],
    ["brush_roller", "brush_roller"],
    ["equipment_panel", "equipment_panel"],
    ["equipment", "equipment"],
    ["weighing_button", "weighing_button"],
    ["panic_button", "panic_button"],
    ["cargo_door", "cargo_door"],
    ["fridge_engine", "fridge_engine"],
    ["grape_conveyor_belt", "grape_conveyor_belt"],
    ["work_enable", "work_enable"],
    ["screen_conveyor_belt", "screen_conveyor_belt"],
    ["bitumen_filling", "bitumen_filling"],
    ["concrete_mixing", "concrete_mixing"],
    ["tractor_uncoupling", "tractor_uncoupling"],
  ];

  return options.map(([key, value]) => ({ label: t(key), value }));
};
